import React from 'react';
import moment from 'moment';
import { useQuery } from '@apollo/client';
import { Home } from '@mui/icons-material';
import { DateFormats } from 'constants/common';
import { BpfLinkType } from '@trnsact/trnsact-shared-types';
import { Box, CircularProgress, IconButton, Stack, Tooltip } from '@mui/material';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';

import { StageTimelineInfo } from './StageTimelineInfo';
import { StagesTimelineProps, StatePipelineRender } from './types';
import { bpfAPI, TimelineResponse, TimelineVariables } from '../../api';

const initialStepLabelByLink: Record<BpfLinkType, string> = {
  VO: 'Opportunity Created',
  CS: 'Submission Created',
};

export const StagesTimeline = ({
  parentId,
  parentType,
  onBackToStages,
  activeTemplateId,
  parentCreatedAt,
}: StagesTimelineProps) => {
  const { data: pipelineResponse, loading } = useQuery<TimelineResponse, TimelineVariables>(
    bpfAPI.fetchStagesTimeline,
    {
      skip: !activeTemplateId,
      variables: {
        parentId,
        parentType,
        departmentId: activeTemplateId,
      },
    }
  );

  const initialStage: StatePipelineRender = {
    label: initialStepLabelByLink[parentType],
    completedAt: moment(parentCreatedAt).format(DateFormats.DateTimeMedium12Hour),
  };

  const stages = pipelineResponse?.bpf?.result?.stages ?? [];

  const completedStages = stages.filter(stage => stage.fieldValues?.isCompleted);
  const inProgressStage = stages[completedStages.length];

  const mappedStagesToRenderView = completedStages.map((stage, index) => {
    const prevStageCompletedDateTime =
      index === 0 ? parentCreatedAt : completedStages[index - 1].fieldValues.completedDateTime;

    return {
      label: stage.name,
      completedBy: stage.fieldValues.completedBy?.fullName,
      completedAt: moment(stage.fieldValues.completedDateTime).format(DateFormats.DateTimeMedium12Hour),
      timeGap: moment(stage.fieldValues.completedDateTime).from(prevStageCompletedDateTime, true),
    };
  }) satisfies StatePipelineRender[];

  const timelineRenderConfig: StatePipelineRender[] = [initialStage, ...mappedStagesToRenderView];

  if (inProgressStage) {
    const prevStageCompletedDateTime =
      completedStages[completedStages.length - 1]?.fieldValues?.completedDateTime ?? parentCreatedAt;

    timelineRenderConfig.push({
      isLastStep: true,
      label: inProgressStage.name,
      timeGap: moment().from(prevStageCompletedDateTime, true),
    });
  }

  return (
    <Stack gap="1rem">
      <Box>
        <Tooltip title="Default view">
          <IconButton size="small" color="primary" className="icon-btn-bordered" onClick={onBackToStages}>
            <Home />
          </IconButton>
        </Tooltip>
      </Box>

      <RenderWithFallback
        isLoading={loading}
        fallBack={
          <Stack alignItems="center" justifyContent="center">
            <CircularProgress />
          </Stack>
        }
      >
        <Stack alignItems="center" justifyContent="center">
          {timelineRenderConfig.map((timelineItem, index) => (
            <StageTimelineInfo
              key={index}
              isFirstItem={!!index}
              timelineItem={timelineItem}
              isLastItem={timelineRenderConfig.length - 1 === index}
            />
          ))}
        </Stack>
      </RenderWithFallback>
    </Stack>
  );
};
